import type { Icon } from "@phosphor-icons/react";
import {
  CaretDown,
  PaperPlaneTilt,
  SignOut,
  UserCheck,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import "@/crmSidebar.css";

export type CrmMenuItem = {
  icon: Icon;
  label: string;
  path: string;
  roles?: string[];
  section?: "top" | "recruiting" | "finance";
};

type SidebarUser = {
  name?: string | null;
  displayName?: string | null;
  email?: string | null;
  picture?: string | null;
};

export function CrmSidebar({
  items,
  activePath,
  user,
  roleLabel,
  disableTransition,
  onNavigate,
  onLogout,
  onImpersonate,
  isAdmin,
  impersonating,
}: {
  items: CrmMenuItem[];
  activePath?: string;
  user: SidebarUser | null;
  roleLabel: string;
  disableTransition?: boolean;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  onImpersonate: () => void;
  isAdmin: boolean;
  impersonating: boolean;
}) {
  const { state, isMobile, toggleSidebar, setOpenMobile } = useSidebar();
  const compact = state === "collapsed" && !isMobile;
  const displayName = user?.displayName || user?.name || "Пользователь";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const navigate = (path: string) => {
    onNavigate(path);
    if (isMobile) setOpenMobile(false);
  };
  const firstGrouped = items.findIndex(
    item => item.section && item.section !== "top"
  );
  const groups = [
    {
      name: "Обзор",
      items:
        firstGrouped < 0
          ? items.filter(item => item.section === "top")
          : items.slice(0, firstGrouped).filter(item => item.section === "top"),
    },
    {
      name: "Рекрутинг",
      items: items.filter(item => item.section === "recruiting"),
    },
    {
      name: "Финансы",
      items: items.filter(item => item.section === "finance"),
    },
    {
      name: "Управление",
      items:
        firstGrouped < 0
          ? []
          : items.slice(firstGrouped).filter(item => item.section === "top"),
    },
  ].filter(group => group.items.length);
  const avatar = (
    <Avatar className="crm-sidebar-avatar">
      {user?.picture && (
        <AvatarImage src={user.picture} referrerPolicy="no-referrer" />
      )}
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  );
  const brand = (
    <>
      <span className="crm-sidebar-brand-mark">
        <UsersThree size={18} weight="regular" aria-hidden />
      </span>
      {!compact && (
        <span className="crm-sidebar-brand-copy">
          <strong>Recruit CRM</strong>
          <span>CHANGE Agency</span>
        </span>
      )}
    </>
  );

  return (
    <Sidebar
      collapsible="icon"
      variant="floating"
      className="crm-sidebar"
      disableTransition={disableTransition}
    >
      <SidebarHeader className="crm-sidebar-header">
        {isMobile ? (
          <div className="crm-sidebar-brand">{brand}</div>
        ) : (
          <button
            type="button"
            className="crm-sidebar-brand"
            onClick={toggleSidebar}
            aria-label={compact ? "Развернуть меню" : "Свернуть меню"}
            title={compact ? "Развернуть меню" : "Свернуть меню"}
          >
            {brand}
          </button>
        )}
        {isMobile && (
          <button
            type="button"
            className="crm-sidebar-close"
            aria-label="Закрыть меню"
            onClick={() => setOpenMobile(false)}
          >
            <X size={18} aria-hidden />
          </button>
        )}
      </SidebarHeader>
      <SidebarContent className="crm-sidebar-content">
        <nav aria-label="Разделы CRM">
          {groups.map(group => (
            <div className="crm-sidebar-group" key={group.name}>
              <SidebarMenu aria-label={group.name}>
                {group.items.map(item => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      className="crm-sidebar-link group-data-[collapsible=icon]:size-9! group-data-[collapsible=icon]:p-2!"
                      tooltip={item.label}
                      isActive={activePath === item.path}
                      aria-current={
                        activePath === item.path ? "page" : undefined
                      }
                      onClick={() => navigate(item.path)}
                    >
                      <item.icon weight="regular" aria-hidden />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </div>
          ))}
        </nav>
      </SidebarContent>
      <SidebarFooter className="crm-sidebar-footer">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="crm-sidebar-profile"
              aria-label="Меню профиля"
            >
              {avatar}
              {!compact && (
                <>
                  <span className="crm-sidebar-profile-copy">
                    <strong>{displayName}</strong>
                    <span>{roleLabel}</span>
                  </span>
                  <CaretDown size={16} aria-hidden />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side={isMobile ? "top" : "right"}
            sideOffset={8}
            className="crm-sidebar-profile-menu"
          >
            <DropdownMenuItem
              onClick={() => navigate("/profile")}
              className="crm-sidebar-profile-summary"
            >
              {avatar}
              <div>
                <strong>{displayName}</strong>
                <span>{user?.email}</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/telegram")}>
              <PaperPlaneTilt size={18} weight="regular" />
              <span>Telegram</span>
            </DropdownMenuItem>
            {(isAdmin || impersonating) && (
              <DropdownMenuItem
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                  onImpersonate();
                }}
              >
                <UserCheck size={18} weight="regular" />
                <span>
                  {impersonating ? "Выйти из имперсонации" : "Войти как..."}
                </span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onLogout}>
              <SignOut size={18} weight="regular" />
              <span>Выйти</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
